<template>
  <div class="mt-4" id="mainPettyCash">
    <div v-if="loading" class="bg-gray-100 animate-pulse rounded-lg p-6 text-gray-400 font-semibold">
      Cargando caja chica...
    </div>

    <div v-else class="space-y-6">
      <div
        v-if="!currentPettyCash.id"
        class="bg-white border rounded-lg overflow-hidden shadow-sm"
      >
        <div class="px-5 py-4 border-b bg-blue-50">
          <h3 class="text-lg font-bold text-blue-700">Apertura de caja chica</h3>
          <p class="text-sm text-blue-500 mt-1">
            La caja chica se maneja de forma independiente a la caja de ventas.
          </p>
        </div>
        <div class="p-5 grid md:grid-cols-2 gap-6">
          <label class="block">
            <span class="block mb-2 text-sm font-medium text-gray-800">
              Monto inicial <span class="text-red-300">*</span>
            </span>
            <input
              type="number"
              min="0"
              class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
              v-model="openingForm.startingAmount"
              placeholder="0.00"
              @keydown.enter.prevent
            />
          </label>
          <label class="block md:col-span-2">
            <span class="block mb-2 text-sm font-medium text-gray-800">
              Comentario de apertura
            </span>
            <textarea
              class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 resize-none w-full border-2 focus:border-blue-500"
              v-model="openingForm.openingComment"
              rows="3"
              maxlength="255"
            ></textarea>
          </label>
          <div class="md:col-span-2 flex justify-end">
            <button
              class="bg-blue-500 text-white px-4 py-2 font-semibold text-sm rounded-lg hover:shadow-sm hover:shadow-blue-600 active:scale-95 flex items-center"
              @click="openPettyCash"
              :disabled="spinnerOpen"
            >
              <div class="flex items-center" v-if="spinnerOpen">
                <svg class="w-4 h-4 mr-3 animate-spin">
                  <use href="../../assets/svg/icon.svg#spinnerForBlue" />
                </svg>
                Abriendo caja chica
              </div>
              <span v-else>Abrir caja chica</span>
            </button>
          </div>
        </div>
      </div>

      <div v-else class="space-y-6">
        <div class="bg-white border rounded-lg shadow-sm p-5">
          <div class="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 class="text-lg font-bold text-gray-800">Gestión de caja chica</h3>
              <p class="text-sm text-gray-500 mt-1">
                Responsable de apertura: <strong>{{ currentPettyCash.usuario_apertura }}</strong>
              </p>
              <p class="text-sm text-gray-500 mt-1">
                Fecha de apertura: <strong>{{ formatDateTime(currentPettyCash.fecha_apertura) }}</strong>
              </p>
              <p class="text-sm text-gray-500 mt-1" v-if="currentPettyCash.comentario_apertura">
                Comentario: <strong>{{ currentPettyCash.comentario_apertura }}</strong>
              </p>
            </div>
            <div class="flex justify-end gap-3 flex-wrap">
              <button
                class="bg-green-500 text-white px-4 py-2 text-sm rounded-lg font-semibold active:scale-95"
                @click="toggleIncreaseBalanceModal"
              >
                Aumentar balance
              </button>
              <button
                class="bg-blue-500 text-white px-4 py-2 text-sm rounded-lg font-semibold active:scale-95"
                @click="toggleRegisterMovement"
              >
                Registrar desembolso
              </button>
            </div>
          </div>
        </div>

        <div class="grid md:grid-cols-4 gap-4">
          <div class="border rounded-lg p-4 bg-white shadow-sm">
            <div class="text-xs text-gray-500 font-semibold uppercase">Monto inicial</div>
            <div class="text-xl font-bold text-gray-700 mt-3">
              {{ money() }} {{ format(currentPettyCash.monto_inicial) }}
            </div>
          </div>
          <div class="border rounded-lg p-4 bg-white shadow-sm">
            <div class="text-xs text-gray-500 font-semibold uppercase">Balance aumentado</div>
            <div class="text-xl font-bold text-blue-600 mt-3">
              {{ money() }} {{ format(totalIncreases) }}
            </div>
          </div>
          <div class="border rounded-lg p-4 bg-white shadow-sm">
            <div class="text-xs text-gray-500 font-semibold uppercase">Total desembolsado</div>
            <div class="text-xl font-bold text-red-500 mt-3">
              {{ money() }} {{ format(currentPettyCash.total_desembolsado) }}
            </div>
          </div>
          <div class="border rounded-lg p-4 bg-white shadow-sm">
            <div class="text-xs text-gray-500 font-semibold uppercase">Balance actual</div>
            <div class="text-xl font-bold text-green-600 mt-3">
              {{ money() }} {{ format(currentPettyCash.balance_actual) }}
            </div>
          </div>
          <div class="border rounded-lg p-4 bg-white shadow-sm">
            <div class="text-xs text-gray-500 font-semibold uppercase">Estado</div>
            <div class="mt-3">
              <span class="px-3 py-1 rounded-lg bg-orange-50 text-orange-600 text-sm font-semibold">
                ABIERTA
              </span>
            </div>
          </div>
        </div>

        <div class="rounded-lg overflow-hidden border bg-white">
          <div class="px-4 py-4 flex justify-between items-center border-b flex-wrap gap-4">
            <div>
              <h3 class="text-lg font-bold text-gray-800">Últimos movimientos</h3>
              <p class="text-sm text-gray-500">
                Balance actual = monto inicial + balance aumentado - total desembolsado
              </p>
            </div>
          </div>
          <div class="w-full resize-x resize-generate" ref="widthNav" @resize="handleResize">
            <div class="overflow-x-auto">
              <table class="table-auto w-full">
                <TableHead :headers="header" />
                <tbody class="text-gray-500 text-left border-t-[1px]" v-if="movements.length > 0">
                  <tr
                    v-for="(movement, index) in movements"
                    :key="index"
                    class="hover:bg-blue-500 transition-colors hover:text-white capitalize font-semibold odd:bg-white even:bg-slate-50"
                    @click="selectRow($event)"
                  >
                    <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap uppercase">
                      <span
                        class="px-3 py-1 rounded-lg text-xs"
                        :class="{
                          'bg-green-50 text-green-600': movement.tipo_movimiento === 'AUMENTO',
                          'bg-red-50 text-red-500': movement.tipo_movimiento === 'DESEMBOLSO',
                        }"
                      >
                        {{ movement.tipo_movimiento === 'AUMENTO' ? 'AUMENTO' : 'DESEMBOLSO' }}
                      </span>
                    </td>
                    <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap uppercase">
                      {{ movement.nombres }}
                    </td>
                    <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap uppercase">
                      {{ movement.numero_referencia || '---' }}
                    </td>
                    <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap uppercase">
                      {{ movement.beneficiario || '---' }}
                    </td>
                    <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap uppercase">
                      {{ movement.departamento || '---' }}
                    </td>
                    <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap">
                      {{ movement.concepto }}
                    </td>
                    <td
                      class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap"
                      :class="{
                        'bg-red-50 text-red-500': movement.tipo_movimiento === 'DESEMBOLSO',
                        'bg-green-50 text-green-600': movement.tipo_movimiento === 'AUMENTO',
                      }"
                    >
                      {{ money() }} {{ format(movement.monto) }}
                    </td>
                    <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap">
                      {{ formatDateTime(movement.fecha_de_desembolso) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div v-if="movements.length === 0" class="px-4 py-8 text-center text-gray-400 font-semibold">
            No hay movimientos registrados en la caja chica activa.
          </div>
        </div>
      </div>
    </div>
  </div>

  <RegisterPettyCashMovement
    v-if="stateRegisterMovement"
    :activeModal="toggleRegisterMovement"
    :getdata="getCurrentPettyCash"
    :balance="currentPettyCash.balance_actual"
  />

  <IncreasePettyCashBalance
    v-if="stateIncreaseBalanceModal"
    :activeModal="toggleIncreaseBalanceModal"
    :getdata="getCurrentPettyCash"
  />
</template>

<script>
import { onMounted, ref, watchEffect } from "vue";
import axios from "@/api";
import { confirmationOfRecord, errorActions } from "@/alerts";
import { observeElement } from "@/observer";
import { idLocalStore, idLocalTurn, idLocalUser } from "@/publicjs/localStorage";
import { money, format } from "@/publicjs/money";
import { formatDateTime } from "@/publicjs/formatDate";
import { selectRow } from "@/publicjs/selectRowTable";
import TableHead from "@/components/TablesComponents/TableHead";
import RegisterPettyCashMovement from "@/components/PettyCash/RegisterPettyCashMovement.vue";
import IncreasePettyCashBalance from "@/components/PettyCash/IncreasePettyCashBalance.vue";

export default {
  name: "MainPettyCash",
  components: {
    TableHead,
    RegisterPettyCashMovement,
    IncreasePettyCashBalance,
  },
  setup() {
    const loading = ref(true);
    const spinnerOpen = ref(false);
    const stateRegisterMovement = ref(false);
    const stateIncreaseBalanceModal = ref(false);
    const currentPettyCash = ref({});
    const movements = ref([]);
    const totalIncreases = ref(0);
    const widthNav = ref(null);
    const idStore = ref(0);
    const idUser = ref(0);
    const idTurn = ref(0);
    const header = [
      [
        { title: "tipo" },
        { title: "responsable" },
        { title: "referencia" },
        { title: "beneficiario" },
        { title: "departamento" },
        { title: "concepto" },
        { title: "monto" },
        { title: "fecha" },
      ],
    ];

    const openingForm = ref({
      startingAmount: "",
      openingComment: "",
    });

    const handleResize = () => {
      const nav = document.querySelector("nav")?.offsetWidth || 0;
      watchEffect(() => {
        if (widthNav.value) {
          widthNav.value.style.width = `calc(100vw - ${nav + 42}px)`;
        }
      });
    };

    const getCurrentPettyCash = async () => {
      loading.value = true;
      const response = await axios
        .get(`caja-chica/actual?idstore=${idStore.value}`)
        .catch((error) => errorActions(error));

      if (!response) {
        loading.value = false;
        return;
      }

      const data = await response.data;
      currentPettyCash.value = data.msg ? data.data : {};
      movements.value = data.msg ? data.movements : [];
      totalIncreases.value = data.msg ? parseFloat(data.summary?.total_aumentado || 0) : 0;
      loading.value = false;
    };

    const openPettyCash = async () => {
      if (
        openingForm.value.startingAmount === "" ||
        openingForm.value.startingAmount === null ||
        parseFloat(openingForm.value.startingAmount) < 0
      ) {
        errorActions("Ingresa un monto inicial válido para la caja chica");
        return;
      }

      spinnerOpen.value = true;
      const response = await axios
        .post(
          "caja-chica/apertura",
          {
            idStore: idStore.value,
            idUser: idUser.value,
            idTurn: idTurn.value,
            startingAmount: parseFloat(openingForm.value.startingAmount),
            openingComment: openingForm.value.openingComment,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        )
        .catch((error) => errorActions(error));

      if (!response) {
        spinnerOpen.value = false;
        return;
      }

      const data = await response.data;
      if (data.msg === true) {
        confirmationOfRecord("La caja chica", {
          title: "Aperturada",
        });
        openingForm.value.startingAmount = "";
        openingForm.value.openingComment = "";
        await getCurrentPettyCash();
      } else if (data.msg === "exist") {
        errorActions("Ya existe una caja chica abierta en la sucursal actual");
      } else {
        errorActions("No se pudo abrir la caja chica");
      }

      spinnerOpen.value = false;
    };

    const toggleRegisterMovement = () => {
      stateRegisterMovement.value = !stateRegisterMovement.value;
    };

    const toggleIncreaseBalanceModal = () => {
      stateIncreaseBalanceModal.value = !stateIncreaseBalanceModal.value;
    };

    onMounted(async () => {
      observeElement("#mainPettyCash");
      handleResize();
      window.addEventListener("resize", handleResize);
      idStore.value = await idLocalStore();
      idUser.value = await idLocalUser();
      idTurn.value = await idLocalTurn();
      await getCurrentPettyCash();
    });

    return {
      loading,
      spinnerOpen,
      openingForm,
      currentPettyCash,
      movements,
      header,
      widthNav,
      handleResize,
      getCurrentPettyCash,
      openPettyCash,
      stateRegisterMovement,
      toggleRegisterMovement,
      stateIncreaseBalanceModal,
      toggleIncreaseBalanceModal,
      totalIncreases,
      money,
      format,
      formatDateTime,
      selectRow,
    };
  },
};
</script>